/**
 * The generic evaluators of the rule catalog's `registry` rules.
 *
 * A `registry` rule is a fact rule: whether a value holds is decided by facts
 * a registry owns (a limit, a list of types, which operators read which
 * source). Each evaluator kind below is one generic check over those facts.
 * None of them knows a rule: the rule is the catalog entry, which names the
 * evaluator and the registry facts it reads
 * (`config/questionnaire-rule-catalog.json`). Python has the same evaluators
 * (`apps/python-api/models/questionnaire/rule_evaluators.py`); every entry's
 * examples (`tests/fixtures/questionnaire-rule-catalog/<code>/`) run through
 * both, so the two cannot disagree.
 *
 * `evaluateRule(evaluator, facts, subject)` returns the failures, in order; an
 * empty list means the rule holds. A failure names its `reason` and carries
 * the facts a message is rendered from, so no caller restates them. A caller
 * passes the facts its registry reader exports; the conformance runner passes
 * the catalog entry's facts (`ruleFact`), and they are the same values.
 */

import { assetRefKeysIn, assetRefToken, replaceAssetRefTokens } from "./asset-ref-tokens";

export type RuleEvaluatorKind =
  | "field_allowed_for_type"
  | "count_within_bounds"
  | "value_in_list"
  | "operator_reads_source"
  | "scale_executable"
  | "asset_tokens_known"
  | "asset_names_tokenized"
  | "condition_fields_read";

export type RuleFailure =
  | { reason: "type_not_allowed"; types: Array<string> }
  | { reason: "below_min"; min: number }
  | { reason: "above_max"; max: number }
  | { reason: "not_in_list"; values: Array<unknown> }
  | { reason: "unknown_operator"; operators: Array<string> }
  | { reason: "value_count"; operator: string; values: ConditionValueCount }
  | { reason: "family_source_type"; family: string; sourceTypes: Array<string> }
  | { reason: "operator_source_type"; operator: string; sourceTypes: Array<string> }
  | { reason: "unsupported_scale"; units: Array<string> }
  | { reason: "point_labels_not_taken"; units: Array<string> }
  | { reason: "point_label_count"; points: number }
  | { reason: "key_unknown"; token: string }
  | { reason: "name_not_token"; name: string }
  | { reason: "field_not_read"; field: string; operator: string };

type ConditionValueCount = "none" | "one" | "one_or_more";

/** The condition-operator registry's families, as the file writes them. */
export type ConditionOperatorFamilies = Readonly<
  Record<
    string,
    {
      sourceTypes: ReadonlyArray<string>;
      values: string;
      operators: Readonly<
        Record<string, { sourceTypes?: ReadonlyArray<string>; values?: string }>
      >;
    }
  >
>;

/** The question-capability registry's per-type facts, as the file writes them. */
export type QuestionCapabilityFlags = Readonly<Record<string, object>>;

/** The rating-scale registry's supported scales, as the file writes them. */
export type RatingScaleFactsByUnit = Readonly<
  Record<string, { points: number; pointLabels: boolean }>
>;

/**
 * The question type sets the per-type `capability` flag that allows a field
 * (`capabilities`: the question-capability registry's `questionTypes`).
 */
export function fieldAllowedForType(
  facts: { capabilities: QuestionCapabilityFlags },
  subject: { questionType: unknown; capability: string },
): Array<RuleFailure> {
  const { capabilities } = facts;
  const types = Object.keys(capabilities).filter(
    (type) =>
      (capabilities[type] as Record<string, unknown>)[subject.capability] === true,
  );
  return types.includes(subject.questionType as string)
    ? []
    : [{ reason: "type_not_allowed", types }];
}

/** `count` is at least `min` and at most `max` (either may be absent). */
export function countWithinBounds(
  facts: { min?: number | null; max?: number | null },
  subject: { count: number },
): Array<RuleFailure> {
  const { min, max } = facts;
  if (min !== undefined && min !== null && subject.count < min) {
    return [{ reason: "below_min", min }];
  }
  if (max !== undefined && max !== null && subject.count > max) {
    return [{ reason: "above_max", max }];
  }
  return [];
}

/** `value` is one of `values` (a list, or a registry map's keys). */
export function valueInList(
  facts: { values: ReadonlyArray<unknown> | Readonly<Record<string, unknown>> },
  subject: { value: unknown },
): Array<RuleFailure> {
  const values: Array<unknown> = Array.isArray(facts.values)
    ? [...facts.values]
    : Object.keys(facts.values);
  return values.includes(subject.value)
    ? []
    : [{ reason: "not_in_list", values }];
}

const VALUE_COUNT_HOLDS: Record<string, (count: number) => boolean> = {
  none: (count) => count === 0,
  one: (count) => count === 1,
  one_or_more: (count) => count >= 1,
};

/**
 * A condition `operator` exists, names as many values as its family says
 * (`valueCount`, when the subject has one), and reads a `sourceType` question
 * (when the subject has one).
 */
export function operatorReadsSource(
  facts: { operators: ConditionOperatorFamilies },
  subject: { operator: unknown; sourceType?: unknown; valueCount?: number },
): Array<RuleFailure> {
  const families = facts.operators;
  const operator = subject.operator as string;
  const family = Object.keys(families).find(
    (name) =>
      typeof operator === "string" &&
      Object.prototype.hasOwnProperty.call(families[name].operators, operator),
  );
  if (family === undefined) {
    return [
      {
        reason: "unknown_operator",
        operators: Object.values(families).flatMap((familyFacts) =>
          Object.keys(familyFacts.operators),
        ),
      },
    ];
  }
  const familyFacts = families[family];
  const own = familyFacts.operators[operator];
  const failures: Array<RuleFailure> = [];
  const values = (own.values ?? familyFacts.values) as ConditionValueCount;
  if ("valueCount" in subject && !VALUE_COUNT_HOLDS[values](subject.valueCount as number)) {
    failures.push({ reason: "value_count", operator, values });
  }
  const sourceType = subject.sourceType;
  if ("sourceType" in subject) {
    if (!familyFacts.sourceTypes.includes(sourceType as string)) {
      failures.push({
        reason: "family_source_type",
        family,
        sourceTypes: [...familyFacts.sourceTypes],
      });
    } else if (
      own.sourceTypes !== undefined &&
      !own.sourceTypes.includes(sourceType as string)
    ) {
      failures.push({
        reason: "operator_source_type",
        operator,
        sourceTypes: [...own.sourceTypes],
      });
    }
  }
  return failures;
}

/**
 * A rating `unit` is a supported scale, and point labels, when given
 * (`pointLabelCount`), are taken by that scale, one per point.
 */
export function scaleExecutable(
  facts: { scales: RatingScaleFactsByUnit },
  subject: { unit: unknown; pointLabelCount?: number | null },
): Array<RuleFailure> {
  const { scales } = facts;
  const unit = subject.unit as string;
  if (
    typeof unit !== "string" ||
    !Object.prototype.hasOwnProperty.call(scales, unit)
  ) {
    return [{ reason: "unsupported_scale", units: Object.keys(scales) }];
  }
  const labelCount = subject.pointLabelCount;
  if (labelCount === undefined || labelCount === null) return [];
  if (!scales[unit].pointLabels) {
    return [
      {
        reason: "point_labels_not_taken",
        units: Object.keys(scales).filter((name) => scales[name].pointLabels),
      },
    ];
  }
  return labelCount === scales[unit].points
    ? []
    : [{ reason: "point_label_count", points: scales[unit].points }];
}

/**
 * Every asset token in `text` names one of `keys` (the study's tested-asset
 * stable keys), read by the token grammar alone (`asset-ref-tokens.ts`).
 */
export function assetTokensKnown(
  _facts: Readonly<Record<string, unknown>>,
  subject: { text: unknown; keys: ReadonlyArray<string> },
): Array<RuleFailure> {
  const keys = new Set(subject.keys);
  return assetRefKeysIn(subject.text)
    .filter((key) => !keys.has(key))
    .map((key) => ({ reason: "key_unknown" as const, token: assetRefToken(key) }));
}

// An `@` that starts a word, as the builder's mention field starts one; an
// e-mail's `@` follows a word character and is no mention.
const MENTION_START = /(?<![@A-Za-z0-9_])@/g;
const WORD_CHARACTER = /[\p{L}\p{N}_]/u;

/**
 * No `@` that starts a word in `text`, outside its tokens, is followed by one
 * of `names` (the tested assets' exact names) as a whole word: an asset is
 * named by its token, and any other `@` (a social handle) is plain text
 * (owner, 2026-10-22).
 */
export function assetNamesTokenized(
  _facts: Readonly<Record<string, unknown>>,
  subject: { text: unknown; names: ReadonlyArray<string> },
): Array<RuleFailure> {
  if (typeof subject.text !== "string") return [];
  const text = replaceAssetRefTokens(subject.text, () => " ");
  const names = [...new Set(subject.names.filter((name) => name.length > 0))].sort(
    (left, right) => right.length - left.length || (left < right ? -1 : left > right ? 1 : 0),
  );
  const failures: Array<RuleFailure> = [];
  for (const match of text.matchAll(MENTION_START)) {
    const rest = text.slice((match.index ?? 0) + 1);
    const name = names.find(
      (candidate) =>
        rest.startsWith(candidate) &&
        !(rest.length > candidate.length && WORD_CHARACTER.test(rest[candidate.length])),
    );
    if (name !== undefined) failures.push({ reason: "name_not_token", name });
  }
  return failures;
}

/**
 * Each of a condition's set `fields` (beside `values`) is read by its
 * `operator` (the operator's `fields`, else its family's) or by its
 * `sourceType` (`sourceFields`: a grid's row and measure). An unknown
 * operator is `operator_reads_source`'s to refuse, so it reads nothing here.
 */
export function conditionFieldsRead(
  facts: {
    operators: Readonly<Record<string, { fields?: ReadonlyArray<string>; operators: Readonly<Record<string, { fields?: ReadonlyArray<string> }>> }>>;
    sourceFields: Readonly<Record<string, ReadonlyArray<string>>>;
  },
  subject: { operator: unknown; sourceType?: unknown; fields: ReadonlyArray<string> },
): Array<RuleFailure> {
  const family = Object.values(facts.operators).find(
    (candidate) => typeof subject.operator === "string" && subject.operator in candidate.operators,
  );
  if (family === undefined) return [];
  const operator = subject.operator as string;
  const read = new Set([
    ...(family.operators[operator].fields ?? family.fields ?? []),
    ...(typeof subject.sourceType === "string" ? facts.sourceFields[subject.sourceType] ?? [] : []),
  ]);
  return subject.fields
    .filter((field) => !read.has(field))
    .map((field) => ({ reason: "field_not_read" as const, field, operator }));
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AnyEvaluator = (facts: any, subject: any) => Array<RuleFailure>;

export const RULE_EVALUATORS: Readonly<Record<RuleEvaluatorKind, AnyEvaluator>> =
  Object.freeze({
    field_allowed_for_type: fieldAllowedForType,
    count_within_bounds: countWithinBounds,
    value_in_list: valueInList,
    operator_reads_source: operatorReadsSource,
    scale_executable: scaleExecutable,
    asset_tokens_known: assetTokensKnown,
    asset_names_tokenized: assetNamesTokenized,
    condition_fields_read: conditionFieldsRead,
  });

/** The failures of `subject` under `evaluator` reading `facts`; [] when it holds. */
export function evaluateRule(
  evaluator: RuleEvaluatorKind,
  facts: Readonly<Record<string, unknown>>,
  subject: Readonly<Record<string, unknown>>,
): Array<RuleFailure> {
  return RULE_EVALUATORS[evaluator](facts, subject);
}
